import * as React from "react";

import {
      brandAuthTitleClassName,
      brandBodyClassName,
      brandDialogDescriptionClassName,
      brandDialogTitleClassName,
      brandDisplayFontStyle,
      brandEyebrowClassName,
      brandPageTitleClassName,
} from "@/components/ui/brand-typography";
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface AuthHeaderProps {
      title: React.ReactNode;
      description: React.ReactNode;
      className?: string;
      eyebrow?: React.ReactNode;
      logoAlt?: string;
      logoSrc?: string;
      showLogo?: boolean;
}

export function AuthHeader({
      title,
      description,
      className,
      eyebrow = "Upsearch",
      logoAlt = "Upsearch",
      logoSrc = "/brand/logo/upsearch-logo-v1-full-bg.png",
      showLogo = true,
}: AuthHeaderProps) {
      return (
            <div className={cn("mb-8", className)}>
                  {showLogo && (
                        <div className="mb-6 flex items-center gap-2.5">
                              <img src={logoSrc} alt={logoAlt} className="h-7 w-7" />
                              <span className={brandEyebrowClassName}>{eyebrow}</span>
                        </div>
                  )}
                  <h1 className={brandAuthTitleClassName}>{title}</h1>
                  <p className={cn(brandBodyClassName, "mt-1.5")}>{description}</p>
            </div>
      );
}

interface OnboardingHeaderProps {
      title: React.ReactNode;
      description: React.ReactNode;
      className?: string;
      note?: React.ReactNode;
      actions?: React.ReactNode;
      titleAs?: "h1" | "h2";
}

export function OnboardingHeader({
      title,
      description,
      className,
      note,
      actions,
      titleAs = "h2",
}: OnboardingHeaderProps) {
      const TitleTag = titleAs;

      return (
            <div className={cn("mb-8", actions && "flex items-start justify-between gap-4", className)}>
                  <div className="min-w-0">
                        <TitleTag className={brandPageTitleClassName} style={brandDisplayFontStyle}>
                              {title}
                        </TitleTag>
                        <p className={cn(brandBodyClassName, "mt-2")}>{description}</p>
                        {note ? (
                              <div className="mt-4 rounded-lg border border-brand-ink/8 bg-brand-ink/[0.025] px-3 py-2 text-xs leading-5 text-brand-ink/45">
                                    {note}
                              </div>
                        ) : null}
                  </div>
                  {actions ? <div className="shrink-0">{actions}</div> : null}
            </div>
      );
}

interface DialogHeaderBlockProps {
      title: React.ReactNode;
      description?: React.ReactNode;
      className?: string;
      titlePrefix?: React.ReactNode;
}

export function DialogHeaderBlock({
      title,
      description,
      className,
      titlePrefix,
}: DialogHeaderBlockProps) {
      return (
            <DialogHeader className={className}>
                  <DialogTitle
                        className={cn(
                              brandDialogTitleClassName,
                              titlePrefix && "flex items-center gap-2",
                        )}
                  >
                        {titlePrefix}
                        {title}
                  </DialogTitle>
                  {description ? (
                        <DialogDescription className={brandDialogDescriptionClassName}>
                              {description}
                        </DialogDescription>
                  ) : null}
            </DialogHeader>
      );
}

interface DashboardPageShellProps {
      children: React.ReactNode;
      className?: string;
      contentClassName?: string;
      as?: React.ElementType;
}

export function DashboardPageShell({
      children,
      className,
      contentClassName,
      as: Component = "main",
}: DashboardPageShellProps) {
      return (
            <Component className={cn("px-3 py-4 sm:px-4", className)}>
                  <div className={cn("min-w-0", contentClassName)}>{children}</div>
            </Component>
      );
}

export function DashboardPageActions({
      children,
      className,
      ...props
}: React.ComponentProps<"div">) {
      return (
            <div className={cn("flex w-full flex-wrap items-center justify-start gap-2 sm:w-auto sm:justify-end", className)} {...props}>
                  {children}
            </div>
      );
}

interface DashboardPageHeaderProps {
      title?: React.ReactNode;
      titleContent?: React.ReactNode;
      description?: React.ReactNode;
      descriptionContent?: React.ReactNode;
      meta?: React.ReactNode;
      aside?: React.ReactNode;
      below?: React.ReactNode;
      className?: string;
      size?: "display" | "detail";
      titleAs?: "h1" | "h2" | "div";
      titleTrailing?: React.ReactNode;
      descriptionClassName?: string;
}

export function DashboardPageHeader({
      title,
      titleContent,
      description,
      descriptionContent,
      meta,
      aside,
      below,
      className,
      size = "display",
      titleAs = "h1",
      titleTrailing,
      descriptionClassName,
}: DashboardPageHeaderProps) {
      const TitleTag = titleAs;
      const titleClassName =
            size === "display"
                  ? "text-3xl font-black leading-none tracking-tight text-brand-ink sm:text-4xl"
                  : "text-2xl font-semibold tracking-tight text-brand-ink sm:text-3xl";
      const defaultDescriptionClassName =
            size === "display"
                  ? "text-sm leading-relaxed text-brand-ink/70"
                  : "text-sm text-muted-foreground";

      return (
            <div className={cn("space-y-4 ", className)}>
                  {meta ? <div className="text-xs text-muted-foreground">{meta}</div> : null}
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0 space-y-2">
                              {titleContent ? (
                                    titleContent
                              ) : (
                                    <TitleTag
                                          className={cn(
                                                titleClassName,
                                                titleTrailing && "flex flex-wrap items-center gap-3",
                                          )}
                                    >
                                          {title}
                                          {titleTrailing}
                                    </TitleTag>
                              )}
                              {descriptionContent ? (
                                    descriptionContent
                              ) : description ? (
                                    <p className={cn(defaultDescriptionClassName, descriptionClassName)}>
                                          {description}
                                    </p>
                              ) : null}
                        </div>
                        {aside ? <div className="flex w-full shrink-0 flex-wrap items-center gap-2 md:w-auto md:justify-end">{aside}</div> : null}
                  </div>
                  {below ? <div>{below}</div> : null}
            </div>
      );
}
